import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import Header from "../Components/Header";
import IconBack from "../Icons/IconBack";
import IconBookmark from "../Icons/IconBookmark";
import IconBookmarkOff from "../Icons/IconBookmarkOff";
import { useFontPinch } from '../Util/useFontPinch';
import { getBookmark, setBookmark, removeBookmark } from '../Util/Bookmarks';

// תאריך מקומי בפורמט YYYY-MM-DD (לא toISOString - שסוטה יום אחורה אחרי חצות)
const dateStr = (d) => (d || new Date()).toLocaleDateString('en-CA');

const PageTemplate = (props) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const ref = useRef()
    const stateFontSize = useFontPinch(ref);
    const [stateBmMenu, setStateBmMenu] = useState(null);  // { x, y, target }

    // לחיצה ארוכה על אלמנט בתוכן פותחת תפריט 'הוסף סימניה'
    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let timer = null, startX = 0, startY = 0, target = null;
        const open = () => {
            timer = null;
            window.getSelection()?.removeAllRanges();
            target.classList.add('bookmark-preview');  // סימון מקדים - שהמשתמש יראה איפה תמוקם הסימניה
            setStateBmMenu({ x: startX, y: startY, target });
        };
        const clear = () => { clearTimeout(timer); timer = null; };
        const down = (e) => {
            if (!e.isPrimary || e.button !== 0) return;
            // סימניה אפשרית רק על אלמנטים שסומנו כברי-סימון בתוכן
            target = e.target.closest('[data-bookmark]');
            if (!target) return;
            startX = e.clientX; startY = e.clientY;
            timer = setTimeout(open, 600);
        };
        const move = (e) => {
            if (timer && (Math.abs(e.clientX - startX) > 10 || Math.abs(e.clientY - startY) > 10)) clear();
        };
        // באנדרואיד לחיצה ארוכה מפעילה contextmenu לפני הטיימר - נתפוס אותה במקומו
        const context = (e) => { if (timer) { e.preventDefault(); clear(); open(); } };

        el.addEventListener('pointerdown', down);
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerup', clear);
        el.addEventListener('pointercancel', clear);
        el.addEventListener('contextmenu', context);
        return () => {
            clear();
            el.removeEventListener('pointerdown', down);
            el.removeEventListener('pointermove', move);
            el.removeEventListener('pointerup', clear);
            el.removeEventListener('pointercancel', clear);
            el.removeEventListener('contextmenu', context);
        };
    }, []);

    // הגעה דרך אייקון הסימניה: גלילה אל האלמנט המסומן והדגשתו
    useEffect(() => {
        // React משתמש מחדש באותם אלמנטים בין ימים - מנקים הדגשות קודמות בכל החלפת יום
        document.querySelectorAll('.bookmark-highlight, .bookmark-preview')
            .forEach((el) => el.classList.remove('bookmark-highlight', 'bookmark-preview'));

        if (!location.state?.gotoBookmark) return;
        const bm = getBookmark(params.type);
        if (!bm || bm.date !== dateStr(props.date)) return;  // ההדגשה שייכת רק ליום שבו נוצרה הסימניה
        const el = ref.current?.querySelectorAll('[data-bookmark]')[bm.index];
        if (el) {
            el.classList.add('bookmark-highlight');
            el.scrollIntoView({ block: 'center' });
            // טעינת הפונט משנה את מידות הטקסט ומזיזה את הפריסה - גוללים שוב כשהפונטים מוכנים
            document.fonts?.ready?.then(() => el.scrollIntoView({ block: 'center' }));
        }
    }, [location.state, params.type, props.date, props.children]);  // children - שהאפקט ירוץ שוב אחרי שהתוכן האסינכרוני נטען

    const closeBmMenu = () => {
        stateBmMenu.target.classList.remove('bookmark-preview');
        setStateBmMenu(null);
    };

    const addBookmark = () => {
        const index = Array.prototype.indexOf.call(ref.current.querySelectorAll('[data-bookmark]'), stateBmMenu.target);
        setBookmark(params.type, { date: dateStr(props.date), index });

        document.querySelector('.bookmark-highlight')?.classList.remove('bookmark-highlight');
        stateBmMenu.target.classList.add('bookmark-highlight');
        closeBmMenu();
    };

    const delBookmark = () => {
        removeBookmark(params.type);
        document.querySelector('.bookmark-highlight')?.classList.remove('bookmark-highlight');
        closeBmMenu();
    };

    // האם הלחיצה היא על האלמנט שכבר מסומן (אותו יום ואותו מקום) - אז מציעים הסרה במקום הוספה
    const isOnBookmark = () => {
        const bm = getBookmark(params.type);
        return bm && bm.date === dateStr(props.date) && ref.current.querySelectorAll('[data-bookmark]')[bm.index] === stateBmMenu.target;
    };

    return (
        <div className="shiur-main"  >
            <Header date={ props.date } setDate={ props.setDate } />
            <div className="shiur-main shiur-content" ref={ref} style={ { fontSize: `${ stateFontSize }px`, touchAction: 'pan-y' } }>{ props.children }</div>
            <a className="menu-float-icon" onClick={ () => { navigate('/'); window.scrollTo(0,0); }}><IconBack /></a>
            { stateBmMenu &&
                <div className='bookmark-overlay' onClick={ closeBmMenu }>
                    <div className='bookmark-menu' style={ { left: Math.min(Math.max(stateBmMenu.x, 100), window.innerWidth - 100), top: Math.max(stateBmMenu.y, 60) } }>
                        { isOnBookmark()
                            ? <button onClick={ delBookmark }><IconBookmarkOff /> הסר סימניה</button>
                            : <button onClick={ addBookmark }><IconBookmark /> הוסף סימניה</button>
                        }
                    </div>
                </div>
            }
        </div>
    );
};

export default PageTemplate;
